/* eslint-disable */

// Brief F §I7.4 — DONE_STAGE/DONE_STATUS now reflect canonical V2 vocabulary
// (Brief C migration unified Done into stage_status='Done' + full_status='Colors received').
// Excel-key lookup tables below preserve original lowercase keys (David's
// historical Excel column values) but emit canonical pairs.
export const DONE_STAGE = 'Done';
export const DONE_STATUS = 'Colors received';

/**
 * T-1039 — Mapping from Excel STAGE cell string → system { stage_status, full_status } pair.
 * Keys are normalized to lowercase + trimmed for case-insensitive lookup (Q14.4).
 * If you add a new allowed Excel string in the future, add it here only.
 *
 * Brief F §I7.4 — values updated to canonical V2 vocabulary. Excel key names
 * unchanged (David's users continue typing the same Excel cell values).
 */
export const EXCEL_STAGE_MAP = {
  'drawing stage':                { stage_status: 'Drawing stage',          full_status: '1st drawing' },
  'waiting for color samples':    { stage_status: 'Color sample stage',     full_status: 'Color samples requested' },
  'hand made sample arrived':     { stage_status: 'Hand made sample stage', full_status: 'Proto / Hand made received' },
  'color sample arrived - done':  { stage_status: DONE_STAGE,               full_status: DONE_STATUS },
};

/**
 * T-1039 — Canonical default for rows with missing STAGE column or invalid STAGE cell.
 * Per Q14.3: invalid values silently coerce to DONE with a visible amber chip
 * surfaced in the conflict card (rendered there, not here).
 */
export const DEFAULT_STAGE_PAIR = {
  stage_status: DONE_STAGE,
  full_status:  DONE_STATUS,
};

/**
 * T-1039 — Dropdown options for the per-row STAGE override in DevConflictCard.
 * Keyed by short id so the conflict card can find the "done" option without
 * relying on string-equality against the full_status (which collides with the
 * "Color sample stage" in-progress option).
 *
 * Brief F §I7.4 — canonical V2 vocabulary throughout.
 */
export const STAGE_OPTIONS = [
  { key: 'drawing', stage_status: 'Drawing stage',          full_status: '1st drawing',                              label: 'Drawing stage' },
  { key: 'hand',    stage_status: 'Hand made sample stage', full_status: 'Proto / Hand made received',               label: 'Hand made sample stage' },
  { key: 'color',   stage_status: 'Color sample stage',     full_status: 'Color samples requested',                  label: 'Color sample stage' },
  { key: 'done',    stage_status: DONE_STAGE,               full_status: DONE_STATUS,                                label: 'Done (Color sample confirmed)' },
];

/**
 * T-1039 — Resolve a STAGE_OPTIONS key from a { stage_status, full_status } pair.
 * Brief F §I7.4 — "Done" is now distinguished by stage_status='Done' (V2 canonical),
 * not by full_status match on the legacy 'Color samples confirmed ✓ — DONE' string.
 */
export function stageKeyFor(stage_status, full_status) {
  if (stage_status === 'Done')                   return 'done';
  if (stage_status === 'Drawing stage')          return 'drawing';
  if (stage_status === 'Hand made sample stage') return 'hand';
  if (stage_status === 'Color sample stage')     return 'color';
  return 'done';
}

/**
 * T-1039 — Look up a raw Excel STAGE cell value against EXCEL_STAGE_MAP.
 * Returns { pair, wasValid, original }. Always returns a usable pair.
 *   - wasValid=true:  exact case-insensitive match → conflict card hides the warning chip
 *   - wasValid=false: cell was non-empty but didn't match → coerce to DEFAULT_STAGE_PAIR
 *                      conflict card shows the amber "Auto-corrected" chip
 *   - empty/null cell: wasValid=true, original='' (no chip; banner may render separately)
 *
 * Q14.4: lookup is case-insensitive + trimmed (the EXCEL_STAGE_MAP keys are lowercased).
 */
export function lookupExcelStage(rawValue) {
  if (rawValue === undefined || rawValue === null) {
    return { pair: DEFAULT_STAGE_PAIR, wasValid: true, original: '' };
  }
  const trimmed = String(rawValue).trim();
  if (!trimmed) {
    return { pair: DEFAULT_STAGE_PAIR, wasValid: true, original: '' };
  }
  const key = trimmed.toLowerCase();
  if (EXCEL_STAGE_MAP[key]) {
    return { pair: EXCEL_STAGE_MAP[key], wasValid: true, original: trimmed };
  }
  return { pair: DEFAULT_STAGE_PAIR, wasValid: false, original: trimmed };
}

export const REQUIRED_COLS = [
  { header: 'development item', field: 'item_code', label: 'Development item' },
  { header: 'factory', field: 'factory', label: 'Factory' },
  { header: 'designer', field: 'designer', label: 'Designer' },
  { header: 'open date', field: 'open_date', label: 'Open date' },
];

export const OPTIONAL_COLS = [
  { header: 'material', field: 'material', label: 'Material' },
  { header: 'season', field: 'season', label: 'Season' },
  { header: 'shape', field: 'shape', label: 'Shape' },
  { header: 'stage', field: 'stage', label: 'Stage' },
];

// Returns { iso10, isoFull } — iso10 is YYYY-MM-DD; isoFull is a UTC-midnight ISO string.
// Handles Date objects, Excel serial numbers, US-format strings (M/D/YYYY, MM/DD/YY),
// and ISO strings. Returns nulls on unparseable input — caller falls back to now().
function parseDate(v) {
  if (v === null || v === undefined || v === '') return { iso10: null, isoFull: null };

  if (v instanceof Date && !isNaN(v.getTime())) {
    const iso10 = v.toISOString().slice(0, 10);
    return { iso10, isoFull: `${iso10}T00:00:00.000Z` };
  }

  if (typeof v === 'number' && isFinite(v)) {
    // Excel epoch: 1899-12-30 (handles the 1900 leap-year bug correctly)
    const ms = Math.round((v - 25569) * 86400 * 1000);
    const d = new Date(ms);
    if (!isNaN(d.getTime())) {
      const iso10 = d.toISOString().slice(0, 10);
      return { iso10, isoFull: `${iso10}T00:00:00.000Z` };
    }
  }

  if (typeof v === 'string') {
    const trimmed = v.trim();
    if (!trimmed) return { iso10: null, isoFull: null };

    // US-format M/D/YYYY, MM/DD/YYYY, M/D/YY with / or - separators
    const m = trimmed.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
    if (m) {
      let mm = parseInt(m[1], 10);
      let dd = parseInt(m[2], 10);
      let yyyy = parseInt(m[3], 10);
      if (yyyy < 100) yyyy += 2000; // 2-digit year → 2000+
      if (mm >= 1 && mm <= 12 && dd >= 1 && dd <= 31 && yyyy >= 1900 && yyyy <= 2100) {
        const mmStr = String(mm).padStart(2, '0');
        const ddStr = String(dd).padStart(2, '0');
        const iso10 = `${yyyy}-${mmStr}-${ddStr}`;
        return { iso10, isoFull: `${iso10}T00:00:00.000Z` };
      }
    }

    // ISO format YYYY-MM-DD (already in shape)
    const iso = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) {
      return { iso10: iso[0], isoFull: `${iso[0]}T00:00:00.000Z` };
    }
  }

  return { iso10: null, isoFull: null };
}

function mapHeaders(rawHeaders) {
  const map = {};
  const allCols = [...REQUIRED_COLS, ...OPTIONAL_COLS];
  rawHeaders.forEach((h, i) => {
    const norm = String(h).toLowerCase().trim();
    const col = allCols.find(c => norm === c.header || norm === c.field);
    if (col) map[col.field] = i;
  });
  return map;
}

export function parseRows(sheet, XLSX) {
  const json = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
  if (json.length < 2) return [];
  const headers = json[0];
  const colMap = mapHeaders(headers);
  const missing = REQUIRED_COLS.filter(c => colMap[c.field] === undefined);
  if (missing.length > 0) {
    throw new Error(`Missing required columns: ${missing.map(c => c.label).join(', ')}`);
  }
  const rows = [];
  for (let i = 1; i < json.length; i++) {
    const r = json[i];
    const item_code = String(r[colMap.item_code] || '').trim();
    if (!item_code) continue;
    const _openDate = parseDate(r[colMap.open_date]);
    rows.push({
      row_index: i + 1,
      item_code,
      factory: String(r[colMap.factory] || '').trim(),
      designer: String(r[colMap.designer] || '').trim(),
      open_date: _openDate.iso10 || '',
      open_date_iso: _openDate.isoFull,
      material: colMap.material !== undefined ? String(r[colMap.material] || '').trim() : '',
      season: colMap.season !== undefined ? String(r[colMap.season] || '').trim() : '',
      shape: colMap.shape !== undefined ? String(r[colMap.shape] || '').trim() : '',
      // T-1039 — STAGE column metadata. Per-row but per-file-constant
      // (every row in the same file shares has_stage_column).
      stage_excel_raw: colMap.stage !== undefined ? String(r[colMap.stage] || '').trim() : '',
      has_stage_column: colMap.stage !== undefined,
    });
  }
  return rows;
}
